import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { deviceSoftseeQuickDetailsQuery, type DeviceSoftseeQuickDetailsPage } from '../api/devices'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'
import './DeviceSoftseeQuickDetails.css'

/**
 * Softsee::deviceQuickDetails - "Software summary" din fisa tichetului
 * (softsee/device/quickDetails.php): sumarul, lista Security Issues (CVE),
 * cat de proaspata e baza NVD, ultimele 10 evenimente cu turnul pe 24h.
 * Cele doua desene vin gata desenate de la server (`QuickPanels`), ca in
 * portal - aici doar se pun in pagina.
 */

export function DeviceSoftseeQuickDetails({ id }: ScreenProps) {
  const { data, error } = useQuery(deviceSoftseeQuickDetailsQuery(id))
  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />
  return <QuickDetails page={data} />
}

function na(value: string | null | undefined): string {
  return value ? value : 'n/a'
}

function QuickDetails({ page }: { page: DeviceSoftseeQuickDetailsPage }) {
  const s = page.summary
  return (
    <div className="device-softsee-quick-details">
      <div className="row">
        <div className="col-md-12">
          <h5 className="bordered">Software summary</h5>
        </div>

        <div className="col-md-6" dangerouslySetInnerHTML={{ __html: page.software_chart }} />

        <div className="col-md-6">
          <table width="100%">
            <tbody>
              <Fact label="OPERATING SYSTEM">
                {s ? (
                  <>
                    {s.operating_system} <label className={`label label-${s.os_tone}`}>{s.os_label}</label>
                  </>
                ) : (
                  'n/a n/a'
                )}
              </Fact>
              {/* Macheta veche scria data asta ca atare, nu din baza. */}
              <Fact label="INSTALLATION DATE">20 Jan 2019</Fact>
              <Fact label="DEFAULT BROWSER" width={150}>{na(s?.default_browser)}</Fact>
              <Fact label="OFFICE" width={150}>{na(s?.office)}</Fact>
              <Fact label="EMAIL CLIENT" width={150}>{na(s?.default_mail_client)}</Fact>
              <Fact label="LICENSED">{page.licensed}</Fact>
              <Fact label="SUSPECT APPLICATIONS" last>{page.suspected}</Fact>
            </tbody>
          </table>
        </div>
      </div>

      <div className="row" style={{ marginTop: 20 }}>
        <div className="col-md-12">
          <h5 className="bordered">Security Issues</h5>
        </div>
        <div className="col-md-12">
          <table className="table table-settings">
            <thead>
              <tr>
                <th className="align-left">Application</th>
                <th className="align-left" style={{ width: 100 }}>
                  Version
                </th>
                <th className="align-left">Details</th>
              </tr>
            </thead>
            <tbody>
              {page.issues.map((issue, i) => (
                <tr key={i}>
                  <td>{issue.product_name}</td>
                  <td>
                    {issue.version_affected} {issue.version_value}
                  </td>
                  <td>{issue.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className={`cve-freshness cve-freshness-${page.cve_state.tone}`}>
            <i className={`fas ${page.cve_state.tone === 'ok' ? 'fa-database' : 'fa-exclamation-triangle'}`}></i>
            <span>
              {page.cve_state.text}
              {page.cve_state.hint !== '' ? <span className="cve-freshness-hint">{page.cve_state.hint}</span> : null}
            </span>
          </p>
        </div>
      </div>

      <div className="row">
        <div className="col-md-12" style={{ marginTop: 25 }}>
          <h5 className="bordered">
            Events <small>(*last 10)</small>
          </h5>
          {page.events.length === 0 ? (
            <div className="alert alert-info">No events</div>
          ) : (
            <table width="100%">
              <tbody>
                <tr>
                  <td style={{ verticalAlign: 'top', width: 150 }} dangerouslySetInnerHTML={{ __html: page.events_chart }} />
                  <td style={{ verticalAlign: 'top' }}>
                    <table className="table table-settings">
                      <thead>
                        <tr>
                          <th className="align-left">Date</th>
                          <th className="align-left">Message</th>
                          <th className="align-left">User</th>
                        </tr>
                      </thead>
                      <tbody>
                        {page.events.map((event, i) => (
                          <tr key={`${event.date}-${i}`}>
                            <td>{event.date}</td>
                            {/* Mesajul vine cu HTML din jurnal (<strong>), ca in macheta veche. */}
                            <td dangerouslySetInnerHTML={{ __html: event.message }} />
                            <td>{event.user}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}

function Fact({ label, width, last, children }: { label: string; width?: number; last?: boolean; children: ReactNode }) {
  const border = last ? undefined : '1px solid #DDD'
  return (
    <tr>
      <td className="font-10" style={{ borderBottom: border, padding: '5px 0' }} width={width}>
        <b>{label}</b>
      </td>
      <td style={{ borderBottom: border, padding: '5px 0 5px 5px', color: '#AAA' }} className="align-left">
        {children}
      </td>
    </tr>
  )
}
